import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { AuthContext } from '@/context/auth.context';
import type { GetUiSectionConfigResponse } from '@erp/rpc';

/**
 * The backend resolves a section config from the *session's active org*, not
 * from any input — so the cache MUST be org-scoped. Without this, a config
 * fetched under one org can be served for another after an in-app org switch
 * (multi-org users), making one org's edits look like they "vanished" when the
 * editor is actually showing a different org's saved config.
 */
function orgScopedGetKey(
  sectionId: string,
  organizationId: string | undefined,
) {
  const baseKey = orpc.core.orgUiSectionConfigs.get.queryKey({
    input: { sectionId },
  });
  return [...baseKey, { org: organizationId ?? 'unknown' }];
}

export function useOrgUiSectionConfig(sectionId: string) {
  const { currentUser } = useContext(AuthContext);
  const options = orpc.core.orgUiSectionConfigs.get.queryOptions({
    input: { sectionId },
  });
  return useQuery({
    ...options,
    queryKey: orgScopedGetKey(sectionId, currentUser?.organizationId),
  });
}

/** Code-registered resolvers + extractors available to assign to a field. */
export function useUiConfigCatalog() {
  return useQuery(
    orpc.core.orgUiSectionConfigs.getCatalog.queryOptions({ input: {} }),
  );
}

export function useUpsertOrgUiSectionConfig() {
  const queryClient = useQueryClient();
  const { currentUser } = useContext(AuthContext);

  return useMutation({
    mutationFn: rpcClient.core.orgUiSectionConfigs.upsert,
    onSuccess: (data, variables) => {
      queryClient.setQueryData<GetUiSectionConfigResponse>(
        orgScopedGetKey(variables.sectionId, currentUser?.organizationId),
        {
          sectionType: data.sectionType,
          fields: data.fields,
          layout: data.layout ?? null,
          config: data.config ?? null,
        },
      );
    },
  });
}
